import { createAsyncThunk } from '@reduxjs/toolkit';
import { LogisticsApi } from '@/Api/Logistics/LogisticsApi';
import { GetDetailedLogisticInfoResponse } from '@/Api/Logistics/Responses/GetDetailedLogisticInfoResponse';
import { GetDetailedLogisticInfoRequest } from '@/Api/Logistics/Requests/GetDetailedLogisticInfoRequest';
import { LogisticContractsApi } from '@/Api/LogisticContracts/LogisticContractsApi';
import { GetGroupedUserWarehouseResponse } from '@/Api/LogisticContracts/Responses/GetGroupedUserWarehouseResponse';
import { GetLogisticsByWarehouseResponse } from '@/Api/Logistics/Responses/GetLogisticsByWarehouseResponse';
import { GetLogisticsByWarehouseRequest } from '@/Api/Logistics/Requests/GetLogisticsByWarehouseRequest';
import { UpdateLogisticContractStateResponse } from '@/Api/LogisticContracts/Responses/UpdateLogisticContractStateResponse';
import { UpdateLogisticContractStateRequest } from '@/Api/LogisticContracts/Requests/UpdateLogisticContractStateRequest';
import { GetLogisticContractResponse } from '@/Api/LogisticContracts/Responses/GetLogisticContractResponse';
import { DeleteLogisticContractRequest } from '@/Api/LogisticContracts/Requests/DeleteLogisticContractRequest';
import { UpdateDeutschePostContractStateResponse } from '@/Api/LogisticDeutschePost/Responses/UpdateDeutschePostContractStateResponse';
import { UpdateDeutschePostContractStateRequest } from '@/Api/LogisticDeutschePost/Requests/UpdateDeutschePostContractStateRequest';
import { LogisticAuthApi } from '@/Api/LogisticAuth/LogisticAuth';
import { UpsGenerateUserAuthUrlResponse } from '@/Api/LogisticAuth/Responses/UpsGenerateUserAuthUrlResponse';
import { CreateLogisticContractResponse } from '@/Api/LogisticContracts/Responses/CreateLogisticContractResponse';
import { CreateLogisticContractRequest } from '@/Api/LogisticContracts/Requests/CreateLogisticContractRequest';
import { UpdateLogisticContractResponse } from '@/Api/LogisticContracts/Responses/UpdateLogisticContractResponse';
import { UpdateLogisticContractRequest } from '@/Api/LogisticContracts/Requests/UpdateLogisticContractRequest';
import { CheckLogisticContractResponse } from '@/Api/LogisticContracts/Responses/CheckLogisticContractResponse';
import { CheckLogisticContractRequest } from '@/Api/LogisticContracts/Requests/CheckLogisticContractRequest';
import { GetDeutschePostContractResponse } from '@/Api/LogisticDeutschePost/Responses/GetDeutschePostContractResponse';
import { LogisticDeutschePostApi } from '@/Api/LogisticDeutschePost/LogisticDeutschePostApi';
import { GetDeutschePostFormatsResponse } from '@/Api/LogisticDeutschePost/Responses/GetDeutschePostFormatsResponse';
import { CreateDeutschePostContractResponse } from '@/Api/LogisticDeutschePost/Responses/CreateDeutschePostContractResponse';
import { CreateDeutschePostContractRequest } from '@/Api/LogisticDeutschePost/Requests/CreateDeutschePostContractRequest';
import { UpdateDeutschePostContractResponse } from '@/Api/LogisticDeutschePost/Responses/UpdateDeutschePostContractResponse';
import { UpdateDeutschePostContractRequest } from '@/Api/LogisticDeutschePost/Requests/UpdateDeutschePostContractRequest';
import { GetDeutschePostContractStatusResponse } from '@/Api/LogisticDeutschePost/Responses/GetDeutschePostContractStatusResponse';
import { GetDeutschePostContractStatusRequest } from '@/Api/LogisticDeutschePost/Requests/GetDeutschePostContractStatusRequest';
import { GetAvailableLogisticsResponse } from '@/Api/Logistics/Responses/GetAvailableLogisticsResponse';
import { GetAvailableLogisticsRequest } from '@/Api/Logistics/Requests/GetAvailableLogisticsRequest';
import { UserProductsApi } from '@/Api/UserProducts/UserProductsApi';
import { GetUserProductTypesResponse } from '@/Api/UserProducts/Responses/GetUserProductTypesResponse';
import { GetUserProductTypesRequest } from '@/Api/UserProducts/Requests/GetUserProductTypesRequest';
import { GetLogisticLabelFormatsRequest } from '@/Api/LogisticContracts/Requests/GetLogisticLabelFormatsRequest';

const getGroupedUserWarehouses = createAsyncThunk<GetGroupedUserWarehouseResponse>(
  'LogisticSettings/getGroupedUserWarehouses',
  () => {
    return LogisticContractsApi.getGroupedUserWarehouses();
  },
);

const getContractUserProducts = createAsyncThunk<GetUserProductTypesResponse, GetUserProductTypesRequest>(
  'LogisticSettings/getContractLogisticProducts',
  (request) => {
    return UserProductsApi.getUserProductTypes(request);
  },
);

const getLogisticsByWarehouse = createAsyncThunk<GetLogisticsByWarehouseResponse, GetLogisticsByWarehouseRequest>(
  'LogisticSettings/getLogisticsByWarehouse',
  (request) => {
    return LogisticsApi.getLogisticsByWarehouse(request);
  },
);

const getDetailedLogisticInfo = createAsyncThunk<GetDetailedLogisticInfoResponse, GetDetailedLogisticInfoRequest>(
  'LogisticSettings/getDetailedLogisticInfo',
  (request) => {
    return LogisticsApi.getDetailedLogisticInfo(request);
  },
);

const getAvailableLogistics = createAsyncThunk<GetAvailableLogisticsResponse, GetAvailableLogisticsRequest>(
  'LogisticSettings/getCountryAvailableLogistics',
  (request) => {
    return LogisticsApi.getAvailableLogistics(request);
  },
);

const getLogisticContract = createAsyncThunk<GetLogisticContractResponse, number>(
  'LogisticSettings/getLogisticContract',
  (request) => {
    return LogisticContractsApi.getLogisticContract(request);
  },
);

const getDeutschePostContract = createAsyncThunk<GetDeutschePostContractResponse, number>(
  'LogisticSettings/getDeutschePostContract',
  (request) => {
    return LogisticDeutschePostApi.getContract(request);
  },
);

const getLogisticLabelFormats = createAsyncThunk<GetDeutschePostFormatsResponse, GetLogisticLabelFormatsRequest>(
  'LogisticSettings/getLogisticLabelFormats',
  (request) => {
    return LogisticContractsApi.getLogisticLabelFormats(request);
  },
);

const getDeutschePostLabelFormats = createAsyncThunk<GetDeutschePostFormatsResponse>(
  'LogisticSettings/getDeutschePostLabelFormats',
  () => {
    return LogisticDeutschePostApi.getLabelFormats();
  },
);

const createLogisticContract = createAsyncThunk<CreateLogisticContractResponse, CreateLogisticContractRequest>(
  'LogisticContractSettings/createLogisticContract',
  async (request: CreateLogisticContractRequest) => {
    return await LogisticContractsApi.createLogisticContract(request);
  },
);

const createDeutschePostContract = createAsyncThunk<
  CreateDeutschePostContractResponse,
  CreateDeutschePostContractRequest
>('LogisticContractSettings/createDeutschePostContract', async (request: CreateDeutschePostContractRequest) => {
  return await LogisticDeutschePostApi.createContract(request);
});

const updateLogisticContract = createAsyncThunk<UpdateLogisticContractResponse, UpdateLogisticContractRequest>(
  'LogisticContractSettings/updateLogisticContract',
  async (request: UpdateLogisticContractRequest) => {
    return await LogisticContractsApi.updateLogisticContract(request);
  },
);

const updateLogisticContractState = createAsyncThunk<
  UpdateLogisticContractStateResponse,
  UpdateLogisticContractStateRequest
>('LogisticSettings/updateLogisticContractState', (request) => {
  return LogisticContractsApi.updateLogisticContractState(request);
});

const updateDeutschePostContractState = createAsyncThunk<
  UpdateDeutschePostContractStateResponse,
  UpdateDeutschePostContractStateRequest
>('LogisticSettings/updateDeutschePostContractState', (request) => {
  return LogisticDeutschePostApi.updateContractState(request);
});

const updateDeutschePostContract = createAsyncThunk<
  UpdateDeutschePostContractResponse,
  UpdateDeutschePostContractRequest
>('LogisticSettings/updateDeutschePostContract', async (request: UpdateDeutschePostContractRequest) => {
  return await LogisticDeutschePostApi.updateContract(request);
});

const checkContractConnection = createAsyncThunk<CheckLogisticContractResponse, CheckLogisticContractRequest>(
  'LogisticContractSettings/checkConnection',
  (request: CheckLogisticContractRequest) => {
    return LogisticContractsApi.checkConnection(request);
  },
);

const checkDeutschePostConnection = createAsyncThunk<
  GetDeutschePostContractStatusResponse,
  GetDeutschePostContractStatusRequest
>('LogisticContractSettings/checkDeutschePostConnection', (request: GetDeutschePostContractStatusRequest) => {
  return LogisticDeutschePostApi.checkConnection(request);
});

const deleteLogisticContract = createAsyncThunk<number, DeleteLogisticContractRequest>(
  'LogisticContractSettings/deleteLogisticContract',
  (request) => {
    return LogisticContractsApi.deleteLogisticContract(request);
  },
);

const deleteDeutschePostContract = createAsyncThunk<number, number>(
  'LogisticContractSettings/deleteDeutschePostContract',
  (request: number) => {
    return LogisticDeutschePostApi.deleteContract(request);
  },
);

const generateUpsUserAuthUrl = createAsyncThunk<UpsGenerateUserAuthUrlResponse>(
  'LogisticContractSettings/generateUpsUserAuthUrl',
  () => {
    return LogisticAuthApi.generateUpsUserAuthUrl();
  },
);

export const asyncActions = {
  getGroupedUserWarehouses,
  getContractUserProducts,
  getDetailedLogisticInfo,
  getLogisticsByWarehouse,
  getAvailableLogistics,
  getLogisticContract,
  getDeutschePostContract,
  getLogisticLabelFormats,
  getDeutschePostLabelFormats,

  createLogisticContract,
  createDeutschePostContract,

  updateLogisticContract,
  updateDeutschePostContract,
  updateLogisticContractState,
  updateDeutschePostContractState,

  checkContractConnection,
  checkDeutschePostConnection,

  deleteLogisticContract,
  deleteDeutschePostContract,

  generateUpsUserAuthUrl,
};
